import { Controller } from "@hotwired/stimulus"

// Fold-out for a folder pin card. There are two variants:
//
//   - Home dashboard (:not(.is-edit)): the card keeps its exact grid cell and
//     the children rise into a floating elevated overlay panel (iOS-folder /
//     macOS-dock feel). The panel is reparented onto the detail-modal host and
//     positioned absolutely there so it escapes the mobile card-pager's overflow
//     clip, still scrolls with the content, and keeps contact detail-modal click
//     delegation working. Zero layout shift — neighbours never move. The panel is
//     the folder card's width with single-column children and opens below the card
//     (flipping above only when there's no room below).
//   - Edit screen (.is-edit): the panel folds inline (CSS grid-rows) so its
//     children stay in flow as live drag-and-drop drop targets. The controller
//     only toggles is-open there; no reparenting or positioning.
//
// Closes on outside click or Escape; the home overlay also repositions on
// resize. The listener pattern mirrors split_menu_controller.

// Floor for the overlay panel's width (px). Wide enough for a real document
// title on one line; still clamped to the host, so a narrow column wins.
const MIN_WIDTH = 260

export default class extends Controller {
    static targets = ["panel", "toggle"]

    connect() {
        this.overlay = !this.element.classList.contains("is-edit")
        // Cache the panel element: reparenting it onto the host (see mountPanel)
        // moves it out of the controller's scope, after which the Stimulus
        // `panel` target no longer resolves — so we hold our own reference.
        this.panelEl = this.hasPanelTarget ? this.panelTarget : null
        this.outsideClick = (event) => {
            const inCard = this.element.contains(event.target)
            const inPanel = this.panelEl && this.panelEl.contains(event.target)
            if (!inCard && !inPanel) this.close()
        }
        this.keydown = (event) => {
            if (event.key === "Escape") this.close()
        }
        this.reposition = () => this.positionPanel()
    }

    disconnect() {
        this.close()
    }

    toggle(event) {
        event?.preventDefault()
        if (this.element.classList.contains("is-open")) {
            this.close()
        } else {
            this.open()
        }
    }

    open() {
        if (this.overlay) this.mountPanel()
        this.element.classList.add("is-open")
        if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-expanded", "true")
        document.addEventListener("click", this.outsideClick)
        document.addEventListener("keydown", this.keydown)
        if (this.overlay) window.addEventListener("resize", this.reposition)
    }

    close() {
        this.element.classList.remove("is-open")
        if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-expanded", "false")
        document.removeEventListener("click", this.outsideClick)
        document.removeEventListener("keydown", this.keydown)
        window.removeEventListener("resize", this.reposition)
        if (this.overlay) this.unmountPanel()
    }

    // Reparent the panel onto the positioning host (the detail-modal container,
    // which sits above the card-pager's overflow clip and scrolls with the
    // content) and position it against the folder card.
    mountPanel() {
        if (!this.panelEl) return
        this.host = this.element.closest("[data-controller~='detail-modal']") || this.element
        if (this.panelEl.parentElement !== this.host) this.host.appendChild(this.panelEl)
        // Once reparented the panel is outside the .home-pin-folder scope, so its
        // floating styling hangs off its own classes.
        this.panelEl.classList.add("home-pin-folder-panel--floating")
        // Expose the child count so the CSS can reverse the rise stagger when the
        // stack opens upward (nearest-the-folder child animates first).
        const grid = this.panelEl.querySelector(".home-pin-folder-grid")
        if (grid) grid.style.setProperty("--count", grid.children.length)
        this.positionPanel()
        this.panelEl.classList.add("is-open")
    }

    unmountPanel() {
        if (!this.panelEl) return
        const panel = this.panelEl
        panel.classList.remove("home-pin-folder-panel--floating", "is-open", "is-above")
        panel.style.top = ""
        panel.style.left = ""
        panel.style.width = ""
        panel.style.removeProperty("--pin-folder-origin")
        const grid = panel.querySelector(".home-pin-folder-grid")
        if (grid) grid.style.removeProperty("--count")
        if (panel.parentElement !== this.element) this.element.appendChild(panel)
    }

    // Anchor the panel to the folder card in the host's coordinate space. The
    // host is position:relative and not itself scrolled, so viewport rects map
    // straight onto its offset box; scrolling the content moves both the card
    // and the (absolute) panel together.
    positionPanel() {
        if (!this.panelEl || !this.hasToggleTarget || !this.host) return
        const gap = 6
        const panel = this.panelEl
        const cardRect = this.element.getBoundingClientRect()
        const hostRect = this.host.getBoundingClientRect()
        const hostWidth = this.host.clientWidth

        // The panel follows the folder card's width so the single-column
        // children read as a stack of cards the same size as the folder — but
        // not below MIN_WIDTH. A chip is as narrow as its own name, and at that
        // width a perfectly ordinary title ("People Ops Playbook") ellipsised
        // two thirds of the way through: the panel is a list you read, not a
        // chip. Still clamped to the host, so it never widens past the column.
        const width = Math.min(Math.max(cardRect.width, MIN_WIDTH), hostWidth)
        panel.style.width = `${Math.round(width)}px`
        let left = cardRect.left - hostRect.left
        left = Math.max(0, Math.min(left, hostWidth - width))
        panel.style.left = `${Math.round(left)}px`

        // Measure the panel height *after* the width is set — the narrow,
        // single-column stack is much taller than a wide grid, and that height
        // drives the above/below decision below.
        const prevDisplay = panel.style.display
        const prevVis = panel.style.visibility
        panel.style.visibility = "hidden"
        panel.style.display = "block"
        const panelHeight = panel.offsetHeight
        panel.style.display = prevDisplay
        panel.style.visibility = prevVis

        // Open BELOW the card, like any menu; flip above only when it doesn't fit
        // below and there's more room above. (It opened above by default while
        // the pins sat at the foot of a bottom-anchored home; on a grid they sit
        // at the top, where opening up ran off the screen.)
        const spaceAbove = cardRect.top
        const spaceBelow = window.innerHeight - cardRect.bottom
        const openDown = spaceBelow >= panelHeight + gap || spaceBelow >= spaceAbove
        if (openDown) {
            panel.classList.remove("is-above")
            panel.style.top = `${Math.round(cardRect.bottom - hostRect.top + gap)}px`
            // Origin at the folder-card edge (top-left) so the pop anchors to the card.
            panel.style.setProperty("--pin-folder-origin", "top left")
        } else {
            panel.classList.add("is-above")
            panel.style.top = `${Math.round(cardRect.top - hostRect.top - panelHeight - gap)}px`
            // Origin at the folder-card edge (bottom-left) so the pop anchors to the card.
            panel.style.setProperty("--pin-folder-origin", "bottom left")
        }
    }
}
